/**
 * ProjectPricingPanel - a project's pricing is EDITED here.
 *
 * The matter's editor (`Matters/economics/MatterPricingPanel`) with everything
 * matter-specific removed: no time-entry check, no revenue warnings, no
 * per-model confirmation - a project has no logged time to invalidate. What is
 * left is the basic level the request asked for: the same five fields, saved
 * and read back.
 *
 * ⚠ BACKEND. The save rides `POST /projects/<uuid>/edit`, which does not carry
 * pricing columns on `dev` yet - the fields are sent and the project record is
 * expected to echo them back. Until the backend stores them the values will
 * not survive a reload. See the Wave 3 tracker note for proj-7.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import { updateProject } from '../../../store/actions/Projects/ProjectActions';
import Button from '../../shared/TailwindComponents/Button';
import PricingFields from '../../shared/economics/PricingFields';
import {
  PricingValue,
  getPricingErrors,
  requiresFixedFee,
  requiresContingency,
} from '../../shared/economics/pricingModel';
import { projectPricingValue, projectPricingPayload } from './projectPricing';

export interface ProjectPricingPanelProps {
  projectUuid: string;
  /** The project record, read for its current pricing fields. */
  project: any;
  /** Receives the saved fields in the project's own snake_case spelling. */
  onSaved?: (patch: Record<string, any>) => void;
  onClose: () => void;
}

const ProjectPricingPanel: React.FC<ProjectPricingPanelProps> = ({
  projectUuid,
  project,
  onSaved,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const initial = useMemo<PricingValue>(() => projectPricingValue(project), [project]);
  const [value, setValue] = useState<PricingValue>(initial);
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const save = async () => {
    if (Object.keys(getPricingErrors(value)).length > 0) { setShowErrors(true); return; }
    setSaving(true);
    try {
      const payload = projectPricingPayload(value);
      const result = await dispatch(updateProject(projectUuid, payload));
      if (!result) return;
      onSaved?.({
        pricing_model: value.pricing_model,
        currency: payload.currency,
        budget_amount: payload.budget_amount,
        ...(requiresFixedFee(value.pricing_model) ? { fixed_fee_amount: payload.fixed_fee_amount } : {}),
        ...(requiresContingency(value.pricing_model) ? { contingency_pct: payload.contingency_pct } : {}),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto custom-scrollbar-main p-5 space-y-4">
        <PricingFields
          value={value}
          onChange={setValue}
          showErrors={showErrors}
          entityNoun={t('project')}
          idPrefix="project"
        />
      </div>
      <div className="border-t border-hairline-soft px-5 py-4 flex justify-end gap-2">
        <Button variant="neutral" size="small" onClick={onClose} disabled={saving}>{t('Cancel')}</Button>
        <Button variant="primary" size="small" onClick={save} disabled={saving}>
          {saving ? t('Saving...') : t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default ProjectPricingPanel;
